import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Users, Search, Upload, Download, FileText, X, AlertCircle,
  CheckCircle, CheckCheck, ChevronLeft, ChevronRight, Trash2, AlertTriangle,
  Edit, Save, Layers, Loader2, Sparkles, RefreshCw, Phone, Calendar, User, Stethoscope
} from 'lucide-react';
import { API_URL as ACTION_API_URL, NODE_API_URL } from '../config';

const API_URL = NODE_API_URL;

const ListOfPatients = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    prescriber_name: '',
    prescriber_phone: '',
    patient_name: '',
    patient_phone: '',
    dob: '',
    insurance_provider: '',
    latest_call_status: ''
  });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Recent-import indicators (persisted across reloads via sessionStorage)
  const [recentImport, setRecentImport] = useState(() => {
    try {
      const raw = sessionStorage.getItem('list_recent_import');
      if (!raw) return { newIds: [], updatedIds: [], newRxIds: [], updatedRxIds: [] };
      const parsed = JSON.parse(raw);
      return {
        newIds: Array.isArray(parsed.newIds) ? parsed.newIds : [],
        updatedIds: Array.isArray(parsed.updatedIds) ? parsed.updatedIds : [],
        newRxIds: Array.isArray(parsed.newRxIds) ? parsed.newRxIds : [],
        updatedRxIds: Array.isArray(parsed.updatedRxIds) ? parsed.updatedRxIds : []
      };
    } catch { return { newIds: [], updatedIds: [], newRxIds: [], updatedRxIds: [] }; }
  });
  const newIdSet = useMemo(() => new Set(recentImport.newIds), [recentImport.newIds]);
  const updatedIdSet = useMemo(() => new Set(recentImport.updatedIds), [recentImport.updatedIds]);
  const hasIndicators = recentImport.newIds.length + recentImport.updatedIds.length > 0;

  const clearImportIndicators = () => {
    sessionStorage.removeItem('list_recent_import');
    setRecentImport({ newIds: [], updatedIds: [], newRxIds: [], updatedRxIds: [] });
  };

  const [showOnlyImported, setShowOnlyImported] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem('list_search') || '');
  const [entriesPerPage, setEntriesPerPage] = useState(() => parseInt(sessionStorage.getItem('list_entries')) || 10);
  const [currentPage, setCurrentPage] = useState(() => parseInt(sessionStorage.getItem('list_page')) || 1);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importPreviewPage, setImportPreviewPage] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [importData, setImportData] = useState({ headers: [], preview: [], totalRecords: 0, csvFile: '' });
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importError, setImportError] = useState('');

  // Clean DB modal state
  const [showCleanModal, setShowCleanModal] = useState(false);
  const [cleanConfirmText, setCleanConfirmText] = useState('');
  const [cleanLoading, setCleanLoading] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('list_search', searchTerm);
    sessionStorage.setItem('list_entries', entriesPerPage);
    sessionStorage.setItem('list_page', currentPage);
  }, [searchTerm, entriesPerPage, currentPage]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/prescriptions/list-all?_t=${Date.now()}`);
      if (Array.isArray(res.data)) setRows(res.data);
    } catch (err) {
      console.error('Error fetching unified list:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refetch when the user returns to this tab/window so updates made
  // inside the Tracker (call statuses) reflect immediately.
  useEffect(() => {
    const onFocus = () => fetchData();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchData(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData]);

  // Status palette mirrors the Tracker page so badges stay consistent.
  const getStatusBadge = (status) => {
    const s = (status || 'PENDING').toUpperCase();
    if (s.includes('NO ANSWER')) return 'bg-red-100 text-red-700';
    if (s.includes('FOLLOW UP')) return 'bg-yellow-100 text-yellow-700';
    if (s.includes('VOICEMAIL')) return 'bg-violet-100 text-violet-700';
    if (s.includes('PROCESS')) return 'bg-emerald-100 text-emerald-700';
    if (s.includes('FAX')) return 'bg-sky-100 text-sky-700';
    if (s.includes('EMAIL')) return 'bg-blue-100 text-blue-700';
    if (s.includes('APPOINTMENT')) return 'bg-indigo-100 text-indigo-700';
    if (s.includes('PATIENT CALL')) return 'bg-cyan-100 text-cyan-700';
    if (s.includes('REQUEST')) return 'bg-purple-100 text-purple-700';
    if (s.includes('NOT FOUND') || s.includes('DATA NOT')) return 'bg-rose-100 text-rose-700';
    if (s.includes('ADDITIONAL')) return 'bg-amber-100 text-amber-700';
    if (s.includes("DON'T NEED") || s.includes('DONT NEED')) return 'bg-gray-200 text-gray-700';
    if (s.includes('DISCONTINUED')) return 'bg-zinc-200 text-zinc-700';
    if (s.includes('INVALID')) return 'bg-red-100 text-red-700';
    if (s.includes('COMPLETED')) return 'bg-green-100 text-green-700';
    if (s.includes('CALL BACK')) return 'bg-orange-100 text-orange-700';
    if (s.includes('PENDING')) return 'bg-slate-100 text-slate-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatDate = (d) => {
    if (!d || d === '0000-00-00') return '';
    const clean = String(d).split(' ')[0].split('T')[0];
    const [y, m, day] = clean.split('-');
    return (y && m && day) ? `${m}/${day}/${y}` : d;
  };

  // Group raw rows by patient (combines duplicate patient entries into one row)
  const groupedPatients = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.patient_id ?? `${(r.patient_name || '').toLowerCase()}|${(r.patient_phone || '')}|${r.patient_dob || ''}`;
      if (!map.has(key)) {
        map.set(key, {
          patient_id: r.patient_id,
          patient_name: r.patient_name,
          patient_phone: r.patient_phone,
          patient_dob: r.patient_dob,
          latest_call_status: r.latest_call_status || '',
          latest_call_at: r.latest_call_at || null,
          prescriptions: [],
          doctors: new Map()
        });
      }
      const g = map.get(key);
      // Same patient should already have a single status (subquery is per
      // patient), but if rows ever disagree pick the most recent one.
      if (r.latest_call_at && (!g.latest_call_at || new Date(r.latest_call_at) > new Date(g.latest_call_at))) {
        g.latest_call_status = r.latest_call_status || g.latest_call_status;
        g.latest_call_at = r.latest_call_at;
      }
      g.prescriptions.push({
        rx_id: r.rx_id, rx_no: r.rx_no, drug_name: r.drug_name,
        doctor_name: r.doctor_name, doctor_phone: r.doctor_phone
      });
      const dKey = (r.doctor_name || '') + '|' + (r.doctor_phone || '');
      if (r.doctor_name && !g.doctors.has(dKey)) {
        g.doctors.set(dKey, { name: r.doctor_name, phone: r.doctor_phone });
      }
    }
    return Array.from(map.values()).map(g => ({
      ...g,
      doctor_list: Array.from(g.doctors.values()),
      rx_count: g.prescriptions.length
    }));
  }, [rows]);

  const filtered = groupedPatients.filter(g => {
    // When "Show new/updated" toggle is on, restrict to recently imported patients
    if (showOnlyImported && hasIndicators) {
      const pid = g.patient_id;
      if (!(newIdSet.has(pid) || updatedIdSet.has(pid))) return false;
    }
    const s = searchTerm.toLowerCase().trim();
    if (!s) return true;
    const cleanDigits = s.replace(/\D/g, '');
    const fields = [
      g.patient_name, g.patient_phone,
      ...g.doctor_list.flatMap(d => [d.name, d.phone]),
      ...g.prescriptions.flatMap(p => [p.drug_name, p.rx_no])
    ].map(v => (v || '').toString().toLowerCase());
    if (fields.some(f => f.includes(s))) return true;
    if (cleanDigits) {
      const phoneDigits = (g.patient_phone || '').replace(/\D/g, '') +
        g.doctor_list.map(d => (d.phone || '').replace(/\D/g, '')).join('');
      if (phoneDigits.includes(cleanDigits)) return true;
      if (g.prescriptions.some(p => (p.rx_no || '').toString().includes(cleanDigits))) return true;
    }
    return false;
  });

  const totalEntries = filtered.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.max(1, Math.ceil(totalEntries / entriesPerPage));
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginated = filtered.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [searchTerm, totalPages, currentPage]);

  const handleDownloadExcel = () => {
    // Build multi-row export: one row per prescription.
    // Patient info (Name, Phone, DOB) only appears on the first row of each patient group.
    const headers = [
      "Patient's Name", "Patient's Phone", "Date of Birth",
      "Doctor", "Doctor Phone", "RX No.", "Drug Name"
    ];

    const allRows = [];
    // Track which rows are the start of each patient group for optional styling
    const patientStartRows = [];

    for (const g of filtered) {
      const prescriptions = g.prescriptions || [];
      if (prescriptions.length === 0) {
        // Patient with no prescriptions — still show one row
        patientStartRows.push(allRows.length);
        allRows.push([
          g.patient_name || '',
          g.patient_phone || '',
          formatDate(g.patient_dob),
          '', '', '', ''
        ]);
        continue;
      }

      for (let i = 0; i < prescriptions.length; i++) {
        const rx = prescriptions[i];
        if (i === 0) {
          // First row — include patient info
          patientStartRows.push(allRows.length);
          allRows.push([
            g.patient_name || '',
            g.patient_phone || '',
            formatDate(g.patient_dob),
            rx.doctor_name || '',
            rx.doctor_phone || '',
            rx.rx_no || '',
            rx.drug_name || ''
          ]);
        } else {
          // Subsequent rows — leave patient columns blank
          allRows.push([
            '', '', '',
            rx.doctor_name || '',
            rx.doctor_phone || '',
            rx.rx_no || '',
            rx.drug_name || ''
          ]);
        }
      }
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...allRows]);

    // Set column widths
    ws['!cols'] = [
      { wch: 28 },  // Patient's Name
      { wch: 18 },  // Patient's Phone
      { wch: 14 },  // Date of Birth
      { wch: 24 },  // Doctor
      { wch: 18 },  // Doctor Phone
      { wch: 14 },  // RX No.
      { wch: 40 }   // Drug Name
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'List of Patients');
    XLSX.writeFile(wb, 'List_of_Patients.xlsx');
  };

  // ===== Edit handlers =====
  const openEdit = async (e, group) => {
    e.stopPropagation();
    if (!group.patient_id) return;
    const firstRx = group.prescriptions[0];
    if (!firstRx) return;
    const firstDoc = group.doctor_list[0] || { name: '', phone: '' };
    setShowEditModal(true);
    setEditLoading(true);
    setEditForm({
      id: firstRx.rx_id,
      prescriber_name: firstDoc.name || '',
      prescriber_phone: firstDoc.phone || '',
      patient_name: group.patient_name || '',
      patient_phone: group.patient_phone || '',
      dob: (group.patient_dob || '').split(' ')[0].split('T')[0],
      insurance_provider: '',
      latest_call_status: group.latest_call_status || 'Pending'
    });
    try {
      const res = await axios.get(`${API_URL}/patients/${group.patient_id}?_t=${Date.now()}`);
      if (res.data?.success && res.data.patient) {
        const p = res.data.patient;
        setEditForm(prev => ({
          ...prev,
          patient_name: p.full_name || prev.patient_name,
          patient_phone: p.phone_number || prev.patient_phone,
          dob: (p.dob || prev.dob || '').split(' ')[0].split('T')[0],
          insurance_provider: p.insurance_provider || prev.insurance_provider,
        }));
      }
    } catch (err) {
      console.error('Load patient error:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const res = await axios.post(`${ACTION_API_URL}?action=update_prescriber_info`, {
        ...editForm,
        dob: editForm.dob || null
      });
      if (res.data?.success) {
        setShowEditModal(false);
        fetchData();
      } else {
        alert('Update failed');
      }
    } catch (err) {
      console.error('Update error:', err);
      alert('Error updating record');
    } finally {
      setEditSaving(false);
    }
  };

  // ===== Delete handlers =====
  const openDelete = (e, group) => {
    e.stopPropagation();
    if (!group.patient_id) return;
    setPatientToDelete(group);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!patientToDelete) return;
    setDeleteLoading(true);
    try {
      const res = await axios.post(`${API_URL}/patients/delete`, { id: patientToDelete.patient_id });
      if (res.data?.success) {
        setShowDeleteModal(false);
        setPatientToDelete(null);
        fetchData();
      } else {
        alert('Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting patient');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ===== Import handlers =====
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Clear the input value so selecting the same file again triggers onChange
    event.target.value = null;

    setUploadedFile(file);
    setImportLoading(true);
    setImportError('');
    setImportMessage('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      let endpoint = '';
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        endpoint = `${API_URL}/import/pdf`;
      } else {
        setImportError('Invalid file type. Please upload a PDF file.');
        setImportLoading(false);
        return;
      }
      const res = await axios.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setImportData(res.data);
        setImportMessage(`File processed. Found ${res.data.totalRecords} records.`);
        setImportStep(2);
      } else {
        setImportError(res.data.error || 'Failed to process file');
      }
    } catch (err) {
      setImportError(err.response?.data?.error || err.message || 'Error uploading file');
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    setImportLoading(true);
    setImportError('');
    try {
      const res = await axios.post(`${API_URL}/import/to-database`, { csvFile: importData.csvFile });
      if (res.data.success) {
        const { imported, updated, skipped, newPatientIds = [], updatedPatientIds = [], newRxIds = [], updatedRxIds = [] } = res.data.result;
        setImportMessage(`${imported} new imported, ${updated} updated, ${skipped} unchanged.`);

        // Merge with any existing indicators so consecutive imports accumulate
        const merged = {
          newIds: Array.from(new Set([...recentImport.newIds, ...newPatientIds])),
          updatedIds: Array.from(new Set([
            ...recentImport.updatedIds,
            ...updatedPatientIds
          ])).filter(id => !newPatientIds.includes(id)),
          newRxIds: Array.from(new Set([...recentImport.newRxIds, ...newRxIds])),
          updatedRxIds: Array.from(new Set([
            ...recentImport.updatedRxIds,
            ...updatedRxIds
          ])).filter(id => !newRxIds.includes(id))
        };
        sessionStorage.setItem('list_recent_import', JSON.stringify(merged));
        setRecentImport(merged);

        setImportStep(3);
        setTimeout(() => {
          fetchData();
          closeImportModal();
        }, 1800);
      } else {
        setImportError(res.data.error || 'Import failed');
      }
    } catch (err) {
      setImportError(err.response?.data?.error || err.message || 'Error importing data');
    } finally {
      setImportLoading(false);
    }
  };

  // ===== Clean Database handler =====
  const handleCleanDatabase = async () => {
    if (cleanConfirmText !== 'CLEAN') return;
    setCleanLoading(true);
    try {
      const res = await axios.post(`${API_URL}/import/clean-database`);
      if (res.data.success) {
        alert('Database cleaned successfully!');
        setShowCleanModal(false);
        setCleanConfirmText('');
        fetchData();
      } else {
        alert('Error cleaning database: ' + (res.data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Clean error:', err);
      alert('System error during cleaning');
    } finally {
      setCleanLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportStep(1);
    setImportError('');
    setImportMessage('');
    setUploadedFile(null);
    setImportData({ headers: [], preview: [], totalRecords: 0, csvFile: '' });
    setImportPreviewPage(1);
  };

  return (
    <div className="relative">
      {/* HEADER CARD */}
      <div className="relative overflow-hidden bg-gradient-to-r from-green-800 to-teal-600 rounded-2xl shadow-lg mb-6 p-6">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 shadow-md">
              <Users className="text-white" size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white tracking-tight">List of Patients</h2>
              <p className="text-xs text-green-100/90 mt-0.5">Manage patient records, prescriptions &amp; doctors</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-green-100/80 font-semibold">Total Patients</p>
              <p className="text-xl font-bold text-white leading-none">{groupedPatients.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* CONTROLS BAR */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search doctor, patient, drug, RX..."
              className="pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition w-full sm:w-80 text-sm shadow-sm"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => fetchData()}
              disabled={loading}
              className="px-3 py-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-700 text-sm font-semibold flex items-center gap-2 shadow-sm transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              title="Refresh latest call statuses from the Tracker"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={() => { setShowImportModal(true); setImportStep(1); setImportError(''); setImportMessage(''); }}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white text-sm font-semibold flex items-center gap-2 shadow-sm hover:shadow-md transition-all whitespace-nowrap"
            >
              <Upload size={14} /> Import PDF
            </button>
            <button
              onClick={() => { setShowCleanModal(true); setCleanConfirmText(''); }}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white text-sm font-semibold flex items-center gap-2 shadow-sm hover:shadow-md transition-all whitespace-nowrap"
            >
              <Trash2 size={14} /> Clean
            </button>
            <button
              onClick={handleDownloadExcel}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold flex items-center gap-2 shadow-sm hover:shadow-md transition-all whitespace-nowrap"
            >
              <Download size={14} /> Excel
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4 px-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select
            value={entriesPerPage}
            onChange={(e) => { setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }}
            className="border-none shadow-sm rounded-full bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 font-medium text-teal-900 cursor-pointer"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        <div className="text-sm text-gray-600">
          Showing {totalEntries === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
        </div>
      </div>

      {hasIndicators && (
        <div className="mb-3 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <div className="text-sm text-emerald-800 flex items-center gap-2 flex-wrap">
            <Sparkles size={16} className="text-emerald-600" />
            <span>
              <strong>{recentImport.newIds.length}</strong> new and{' '}
              <strong>{recentImport.updatedIds.length}</strong> updated patient(s) from the most recent import.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowOnlyImported(v => !v); setCurrentPage(1); }}
              className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${showOnlyImported
                ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-100'}`}
              title={showOnlyImported ? 'Show all patients' : 'Show only patients from the most recent import'}
            >
              {showOnlyImported ? 'Showing new/updated' : 'Show new/updated patients'}
            </button>
            <button
              onClick={() => { clearImportIndicators(); setShowOnlyImported(false); }}
              className="text-xs text-emerald-700 hover:text-emerald-900 underline"
            >
              Clear indicators
            </button>
          </div>
        </div>
      )}

      <div className="bg-white/80 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl overflow-hidden border border-gray-100/50">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className="px-6 py-4 text-left font-bold text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/80 border-b border-gray-100">Patient's Name</th>
                <th className="px-6 py-4 text-left font-bold text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/80 border-b border-gray-100">Phone</th>
                <th className="px-6 py-4 text-left font-bold text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/80 border-b border-gray-100">DOB</th>
                <th className="px-6 py-4 text-left font-bold text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/80 border-b border-gray-100">Doctor(s)</th>
                <th className="px-6 py-4 text-center font-bold text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/80 border-b border-gray-100">RX Count</th>
                <th className="px-6 py-4 text-center font-bold text-slate-500 uppercase tracking-wider text-[11px] bg-slate-50/80 border-b border-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white/50 divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="6" className="text-center py-16"><div className="flex flex-col items-center gap-3 text-gray-400"><Loader2 className="animate-spin text-teal-500" size={28} /><span className="text-sm font-medium">Loading patient records...</span></div></td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-20"><div className="flex flex-col items-center gap-3 text-gray-400"><Users size={40} className="opacity-30" /><span className="italic text-sm font-medium">No records found. Import a PDF to populate this list.</span></div></td></tr>
              ) : (
                paginated.map((g) => {
                  const firstDoc = g.doctor_list[0];
                  const extraDocs = Math.max(0, g.doctor_list.length - 1);
                  return (
                    <tr
                      key={g.patient_id || `${g.patient_name}-${g.patient_phone}`}
                      onClick={() => g.patient_id && navigate(`/patient/${g.patient_id}`)}
                      className="group hover:bg-slate-50/80 transition-all duration-200 cursor-pointer border-b border-gray-50 last:border-0 hover:shadow-[0_2px_10px_rgb(0,0,0,0.02)]"
                      title={g.patient_id ? 'Click to view patient profile' : ''}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-100/50 flex items-center justify-center text-teal-600 font-bold shadow-sm">
                            {g.patient_name ? g.patient_name.charAt(0).toUpperCase() : <User size={16} />}
                          </div>
                          <div className="flex flex-col justify-center">
                            <span className="text-[13px] font-bold text-slate-700 group-hover:text-teal-700 transition-colors">
                              {g.patient_name || <span className="text-gray-300 italic">-</span>}
                            </span>
                            {(newIdSet.has(g.patient_id) || (!newIdSet.has(g.patient_id) && updatedIdSet.has(g.patient_id))) && (
                              <div className="flex gap-2 mt-0.5">
                                {newIdSet.has(g.patient_id) && (
                                  <span className="bg-emerald-100/80 text-emerald-700 border border-emerald-200/60 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide inline-flex items-center gap-1 shadow-sm">
                                    <Sparkles size={8} /> New
                                  </span>
                                )}
                                {!newIdSet.has(g.patient_id) && updatedIdSet.has(g.patient_id) && (
                                  <span className="bg-amber-100/80 text-amber-700 border border-amber-200/60 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide shadow-sm">
                                    Updated
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2.5 text-slate-600 font-medium text-[13px]">
                          <Phone size={14} className="text-slate-400" />
                          {g.patient_phone || <span className="text-gray-300 italic">-</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2.5 text-slate-600 font-medium text-[13px]">
                          <Calendar size={14} className="text-slate-400" />
                          {formatDate(g.patient_dob) || <span className="text-gray-300 italic">-</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {firstDoc ? (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-50/80 border border-indigo-100/50 flex items-center justify-center text-indigo-500 shadow-sm">
                              <Stethoscope size={14} />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[13px] font-semibold text-slate-700">{firstDoc.name}</span>
                              {firstDoc.phone && <span className="text-[11px] text-slate-500 font-medium mt-0.5">{firstDoc.phone}</span>}
                            </div>
                            {extraDocs > 0 && (
                              <span className="ml-1 bg-indigo-100/80 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm border border-indigo-200/50">+{extraDocs}</span>
                            )}
                          </div>
                        ) : <span className="text-gray-300 italic">-</span>}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="bg-teal-50/80 text-teal-700 border border-teal-100/60 px-3 py-1 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1.5 shadow-sm">
                          <Layers size={13} className="opacity-70" /> {g.rx_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-y-1 group-hover:translate-y-0">
                          <button
                            onClick={(e) => openEdit(e, g)}
                            className="bg-white text-teal-600 hover:bg-teal-50 hover:text-teal-700 p-2 rounded-lg border border-teal-100 shadow-sm transition-all hover:shadow-md"
                            title="Edit patient"
                          ><Edit size={15} /></button>
                          <button
                            onClick={(e) => openDelete(e, g)}
                            className="bg-white text-rose-500 hover:bg-rose-50 hover:text-rose-600 p-2 rounded-lg border border-rose-100 shadow-sm transition-all hover:shadow-md"
                            title="Delete patient"
                          ><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex flex-wrap justify-center items-center gap-4 mt-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm font-medium text-gray-600 transition"
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <div className="flex gap-1">
              {[...Array(totalPages)].map((_, idx) => {
                const n = idx + 1;
                if (n === 1 || n === totalPages || (n >= currentPage - 1 && n <= currentPage + 1)) {
                  return (
                    <button
                      key={n}
                      onClick={() => setCurrentPage(n)}
                      className={`min-w-[36px] px-3 py-1.5 rounded-lg text-sm font-semibold transition shadow-sm ${currentPage === n ? 'bg-gradient-to-r from-green-800 to-teal-600 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}
                    >{n}</button>
                  );
                } else if (n === currentPage - 2 || n === currentPage + 2) {
                  return <span key={n} className="px-2 text-gray-400">…</span>;
                }
                return null;
              })}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm font-medium text-gray-600 transition"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>

          <div className="flex items-center gap-2 bg-white border border-gray-200 pl-3 pr-2 py-1.5 rounded-lg shadow-sm">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Jump to:</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="1"
                max={totalPages}
                placeholder="Page"
                className="w-12 px-1.5 py-0.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-center font-extrabold text-gray-700 bg-gray-50 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = parseInt(e.target.value, 10);
                    if (val >= 1 && val <= totalPages) {
                      setCurrentPage(val);
                      e.target.value = '';
                    } else {
                      alert(`Please enter a page between 1 and ${totalPages}`);
                    }
                  }
                }}
              />
              <span className="text-xs text-gray-400 font-medium">/ {totalPages}</span>
            </div>
          </div>
        </div>
      )}

      {/* IMPORT MODAL */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-6 relative">
            <button onClick={closeImportModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>

            <div className="flex justify-center gap-8 mb-6">
              {[1, 2, 3].map(s => (
                <div key={s} className={`flex flex-col items-center ${importStep >= s ? 'opacity-100' : 'opacity-50'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${importStep >= s ? (s === 3 ? 'bg-green-600' : 'bg-blue-600') : 'bg-gray-300'}`}>
                    {s === 3 ? '✓' : s}
                  </div>
                  <span className="text-xs mt-2 font-medium">{s === 1 ? 'Upload' : s === 2 ? 'Preview' : 'Complete'}</span>
                </div>
              ))}
            </div>

            {importError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2 items-start">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-sm text-red-700">{importError}</span>
              </div>
            )}
            {importMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex gap-2 items-start">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-sm text-green-700">{importMessage}</span>
              </div>
            )}

            {importStep === 1 && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Upload className="text-blue-600" /> Upload PDF
                </h3>
                <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center hover:border-blue-500 transition cursor-pointer relative">
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileUpload}
                    disabled={importLoading}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  />
                  <FileText className="mx-auto text-blue-400 mb-3" size={48} />
                  <p className="text-sm text-gray-600">
                    {uploadedFile ? <><strong>{uploadedFile.name}</strong> selected</> : (
                      <>Drag and drop or click to select<br />
                        <span className="text-xs text-gray-500">Supports PDF only (max 50MB)</span>
                      </>
                    )}
                  </p>
                </div>
                {importLoading && <p className="text-center text-blue-600 mt-4">Processing file...</p>}
              </div>
            )}

            {importStep === 2 && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText className="text-blue-600" /> Preview Data
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-600 mb-1"><strong>Total Records:</strong> {importData.totalRecords}</p>
                  <p className="text-sm text-gray-600"><strong>Columns:</strong> {importData.headers.join(', ')}</p>
                </div>
                <div className="border rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        {importData.headers.map((h, i) => (
                          <th key={i} className="px-4 py-2 text-left text-gray-700 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importData.preview.slice((importPreviewPage - 1) * 10, importPreviewPage * 10).map((row, idx) => (
                        <tr key={idx} className="border-t hover:bg-gray-50">
                          {importData.headers.map((h, hi) => (
                            <td key={hi} className="px-4 py-2 text-gray-700 whitespace-nowrap text-xs">{row[h] || '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-gray-500">
                    Showing {(importPreviewPage - 1) * 10 + 1} to {Math.min(importPreviewPage * 10, importData.totalRecords)} of {importData.totalRecords}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setImportPreviewPage(Math.max(1, importPreviewPage - 1))}
                      disabled={importPreviewPage === 1}
                      className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                    ><ChevronLeft size={16} /></button>
                    <span className="text-xs font-medium content-center px-1 border rounded bg-gray-50 w-8 text-center">{importPreviewPage}</span>
                    <button
                      onClick={() => setImportPreviewPage(Math.min(Math.ceil(importData.totalRecords / 10), importPreviewPage + 1))}
                      disabled={importPreviewPage >= Math.ceil(importData.totalRecords / 10)}
                      className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                    ><ChevronRight size={16} /></button>
                  </div>
                </div>
              </div>
            )}

            {importStep === 3 && (
              <div className="text-center">
                <CheckCheck className="mx-auto text-green-600 mb-4" size={64} />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Import Successful!</h3>
                <p className="text-gray-600">Records have been imported and will appear in the list.</p>
              </div>
            )}

            <div className="flex gap-3 mt-6 justify-end">
              {importStep < 3 && (
                <button onClick={closeImportModal} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium">Cancel</button>
              )}
              {importStep === 2 && (
                <button
                  onClick={handleConfirmImport}
                  disabled={importLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {importLoading ? 'Importing...' : (<><CheckCircle size={16} /> Confirm Import</>)}
                </button>
              )}
              {importStep === 3 && (
                <button onClick={closeImportModal} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium">Done</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* EDIT RECORD DETAILS MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-[500px] p-6 relative">
            <button
              onClick={() => setShowEditModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            ><X size={24} /></button>
            <h3 className="text-xl font-bold text-teal-700 mb-4">Edit Record Details</h3>
            {editLoading ? (
              <div className="py-10 text-center text-gray-500 flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={18} /> Loading record...
              </div>
            ) : (
              <form onSubmit={handleEditSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Name</label>
                    <input type="text" className="w-full border p-2 rounded text-sm"
                      value={editForm.prescriber_name}
                      onChange={e => setEditForm({ ...editForm, prescriber_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Phone</label>
                    <input type="text" className="w-full border p-2 rounded text-sm"
                      value={editForm.prescriber_phone}
                      onChange={e => setEditForm({ ...editForm, prescriber_phone: e.target.value })} />
                  </div>
                </div>

                <hr className="border-gray-100 my-2" />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Name</label>
                    <input type="text" className="w-full border p-2 rounded text-sm"
                      value={editForm.patient_name}
                      onChange={e => setEditForm({ ...editForm, patient_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Phone</label>
                    <input type="text" className="w-full border p-2 rounded text-sm"
                      value={editForm.patient_phone}
                      onChange={e => setEditForm({ ...editForm, patient_phone: e.target.value })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">DOB</label>
                    <input type="date" className="w-full border p-2 rounded text-sm"
                      value={editForm.dob}
                      onChange={e => setEditForm({ ...editForm, dob: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Insurance Code</label>
                    <input type="text" className="w-full border p-2 rounded text-sm"
                      value={editForm.insurance_provider}
                      onChange={e => setEditForm({ ...editForm, insurance_provider: e.target.value })} />
                  </div>
                </div>

                <button type="submit" disabled={editSaving}
                  className="w-full bg-teal-600 text-white font-bold py-2 rounded hover:bg-teal-700 flex items-center justify-center gap-2 mt-2 disabled:opacity-50">
                  {editSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                  {editSaving ? 'Updating...' : 'Update Details'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* DELETE PATIENT MODAL */}
      {showDeleteModal && patientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => { setShowDeleteModal(false); setPatientToDelete(null); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            ><X size={24} /></button>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-4">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Patient?</h3>
              <p className="text-sm text-gray-600 mb-4">
                You are about to delete <strong>{patientToDelete.patient_name}</strong> and remove this patient
                from the active list. Associated prescriptions will be hidden as well.
              </p>
            </div>
            <div className="mt-2 flex justify-center gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setPatientToDelete(null); }}
                className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {deleteLoading ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CLEAN DATABASE MODAL */}
      {showCleanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => { setShowCleanModal(false); setCleanConfirmText(''); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-4">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Clean Database</h3>
              <p className="text-sm text-gray-600 mb-4">
                This will permanently remove <strong>all patient and prescription records</strong>.
                This action <strong>cannot be undone</strong>.
              </p>
              <p className="text-xs text-gray-500 mb-2">
                Type <strong className="text-red-600">CLEAN</strong> below to confirm:
              </p>
              <input
                type="text"
                value={cleanConfirmText}
                onChange={(e) => setCleanConfirmText(e.target.value)}
                placeholder="CLEAN"
                className="w-full border border-gray-300 rounded-md p-2 text-center font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => { setShowCleanModal(false); setCleanConfirmText(''); }}
                className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCleanDatabase}
                disabled={cleanConfirmText !== 'CLEAN' || cleanLoading}
                className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {cleanLoading ? 'Cleaning...' : (<><Trash2 size={16} /> Clean Database</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListOfPatients;
